// this component always take and return iso date string
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';

const TimeSpinnerInput = forwardRef(({ value, format12Hour, defaultTime }, ref) => {
  const getInitialTime = () => {
    if (value) {
      const date = new Date(value);

      if (!isNaN(date.getTime())) {
        return {
          date,
          hour: date.getHours().toString().padStart(2, '0'),
          minute: date.getMinutes().toString().padStart(2, '0'),
        };
      }
    }

    if (defaultTime === 'startOfDay') {
      const date = new Date();
      date.setUTCHours(0, 0, 0, 0);
      return {
        date,
        hour: '00',
        minute: '00',
      };
    }

    const now = new Date();

    return {
      date: now,
      hour: now.getHours().toString().padStart(2, '0'),
      minute: now.getMinutes().toString().padStart(2, '0'),
    };
  };

  const [time, setTime] = useState(() => {
    const initial = getInitialTime();

    return {
      date: initial.date,
      hour: initial.hour,
      minute: initial.minute,
    };
  });

  useImperativeHandle(ref, () => ({
    getTime: () => {
      const updated = new Date(time.date);

      updated.setHours(+time.hour);
      updated.setMinutes(+time.minute);
      updated.setSeconds(0);
      updated.setMilliseconds(0);

      return updated.toISOString();
    },
  }));

  const update = (field, value) => {
    let newValue = value.replace(/[^0-9]/g, ''); // keep only numbers

    // prevent empty values
    if (newValue === '') {
      newValue = field === 'hour' ? '00' : '00';
    }

    if (field === 'hour') {
      if (format12Hour) {
        if (+newValue > 12) newValue = '12';
        if (+newValue < 1) newValue = '01';

        // Get current period from existing 24h state
        const period = to12Hour(time.hour).period;
        // Convert 12h input to 24h
        const hour24 = to24Hour(newValue, period);
        setTime((prev) => ({
          ...prev,
          hour: hour24,
        }));
        return;
      }

      // if 24h format
      if (+newValue > 23) newValue = '23';
      if (+newValue < 0) newValue = '00';
      setTime((prev) => ({
        ...prev,
        hour: newValue.padStart(2, '0'),
      }));
      return;
    }

    if (field === 'minute') {
      if (+newValue > 59) newValue = '59';
      if (+newValue < 0) newValue = '00';

      setTime((prev) => ({
        ...prev,
        minute: newValue.padStart(2, '0'),
      }));
    }
  };

  const timeRef = useRef(null);

  const handlePeriodChange = (newPeriod) => {
    const hour12 = to12Hour(time.hour).hour;
    const hour24 = to24Hour(hour12, newPeriod);

    setTime((prev) => ({ ...prev, hour: hour24 }));
  };

  const displayTime12 = format12Hour ? to12Hour(time.hour) : null;

  const displayHour = format12Hour ? displayTime12.hour : time.hour;

  const displayPeriod = format12Hour ? displayTime12.period : null;

  const incrementHour = () => {
    const newH = (+time.hour + 1) % 24;
    setTime((prev) => ({
      ...prev,
      hour: newH.toString().padStart(2, '0'),
    }));
    // Keep focus after updating
    hourRef.current?.focus();
  };

  const decrementHour = () => {
    const newH = (+time.hour - 1 + 24) % 24;
    setTime((prev) => ({
      ...prev,
      hour: newH.toString().padStart(2, '0'),
    }));
    // Keep focus after updating
    hourRef.current?.focus();
  };

  const incrementMinute = () =>
    update('minute', ((+time.minute + 1) % 60).toString().padStart(2, '0'));

  const decrementMinute = () =>
    update('minute', ((+time.minute - 1 + 60) % 60).toString().padStart(2, '0'));

  // Arrow key handling
  const handleKeyDown = (e, type) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();

      if (e.key === 'ArrowUp') {
        type === 'hour' ? incrementHour() : incrementMinute();
      }

      if (e.key === 'ArrowDown') {
        type === 'hour' ? decrementHour() : decrementMinute();
      }
    }
  };

  // Wheel handling
  const hourRef = useRef(null);
  const minuteRef = useRef(null);

  useEffect(() => {
    const hourWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.deltaY < 0 ? incrementHour() : decrementHour();
    };

    const minuteWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.deltaY < 0 ? incrementMinute() : decrementMinute();
    };

    const hourEl = hourRef.current;
    const minuteEl = minuteRef.current;

    hourEl?.addEventListener('wheel', hourWheel, { passive: false });
    minuteEl?.addEventListener('wheel', minuteWheel, { passive: false });

    return () => {
      hourEl?.removeEventListener('wheel', hourWheel);
      minuteEl?.removeEventListener('wheel', minuteWheel);
    };
  }, [time.hour, time.minute]);

  useEffect(() => {
    if (!value) return;

    const date = new Date(value);

    if (!isNaN(date)) {
      setTime({
        date,
        hour: date.getHours().toString().padStart(2, '0'),
        minute: date.getMinutes().toString().padStart(2, '0'),
      });
    }
  }, [value]);

  return (
    <div className='time-input-container'>
      <div className='time-box' ref={timeRef}>
        <input
          id='hour-input'
          ref={hourRef}
          onFocus={(e) => e.target.select()}
          type='text'
          value={displayHour}
          onChange={(e) => update('hour', e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, 'hour')}
          tabIndex={0}
        />
        <span>:</span>
        <input
          id='minute-input'
          ref={minuteRef}
          type='text'
          value={time.minute}
          onFocus={(e) => e.target.select()}
          onChange={(e) => update('minute', e.target.value.replace(/[^0-9]/g, ''))}
          onKeyDown={(e) => handleKeyDown(e, 'minute')}
          tabIndex={0}
        />
      </div>

      {format12Hour && <MeridiemToggle value={displayPeriod} onChange={handlePeriodChange} />}
    </div>
  );
});

TimeSpinnerInput.displayName = 'TimeSpinnerInput';

TimeSpinnerInput.propTypes = {
  value: PropTypes.string.isRequired,
  format12Hour: PropTypes.bool,
  defaultTime: PropTypes.string,
};

export default TimeSpinnerInput;

const MeridiemToggle = ({ value, onChange }) => {
  return (
    <div className='ampm-tabs'>
      {['AM', 'PM'].map((p) => (
        <div
          key={p}
          type='button'
          onClick={() => onChange(p)}
          className={value === p ? 'active' : ''}
        >
          {p}
        </div>
      ))}
    </div>
  );
};

MeridiemToggle.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const to12Hour = (hour24) => {
  const h = +hour24;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;

  return {
    hour: hour12.toString().padStart(2, '0'),
    period,
  };
};

const to24Hour = (hour12, period) => {
  let h = +hour12;

  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;

  return h.toString().padStart(2, '0');
};
